/* ==========================================================================
   Home Tab (Manual Score Sheet, Handicap & Ball Usage Tracker) Controller
   ========================================================================== */

import { appState } from './state.js';

export function initHome() {
  const scorecardHeading = document.getElementById('scorecardHeading');
  const selectMatchWeek = document.getElementById('selectMatchWeek');
  const btnNextWeek = document.getElementById('btnNextWeek');
  const inputOpponent = document.getElementById('inputOpponent');
  const inputHandicap = document.getElementById('inputHandicap');

  const inputGame1 = document.getElementById('inputGame1');
  const inputGame2 = document.getElementById('inputGame2');
  const inputGame3 = document.getElementById('inputGame3');
  const g1HdcpPreview = document.getElementById('g1HdcpPreview');
  const g2HdcpPreview = document.getElementById('g2HdcpPreview');
  const g3HdcpPreview = document.getElementById('g3HdcpPreview');

  const ballSelectGame1 = document.getElementById('ballSelectGame1');
  const ballSelectGame2 = document.getElementById('ballSelectGame2');
  const ballSelectGame3 = document.getElementById('ballSelectGame3');

  const displaySeriesTotal = document.getElementById('displaySeriesTotal');
  const displayHdcpTotal = document.getElementById('displayHdcpTotal');
  const displaySeriesAvg = document.getElementById('displaySeriesAvg');
  const inputPointsWon = document.getElementById('inputPointsWon');
  const btnSaveScores = document.getElementById('btnSaveScores');

  const homeBallName = document.getElementById('homeBallName');
  const homeBallMeta = document.getElementById('homeBallMeta');
  const homeBallAvatar = document.getElementById('homeBallAvatar');
  const notesListEl = document.getElementById('notesList');
  const btnAddNoteInline = document.getElementById('btnAddNoteInline');
  const modalNote = document.getElementById('noteModal');
  const btnCloseNote = document.getElementById('btnCloseNote');
  const btnCancelNote = document.getElementById('btnCancelNote');
  const btnSaveNote = document.getElementById('btnSaveNote');
  const frictionPills = document.querySelectorAll('.friction-pill');
  const advisorTipText = document.getElementById('advisorTipText');

  const frictionTips = {
    fresh: "Lanes are fresh. Solid reactive surface provides strong midlane traction. Target pocket between 17-18 board at breakpoint.",
    mid: "Carrydown detected past 40ft. Ball may push right at backend. Move stance 2 boards right or switch to a higher friction surface.",
    burned: "Heads are drying up. Front friction is high. Move stance deep inside (Board 22-25) into middle oil to preserve energy for pocket.",
    sport: "Flat sport pattern. High accuracy required. Small margin for error; play tight angles near the oil edge."
  };

  let initializedWeek = null;

  // Initialize Week Dropdown (Weeks 1 through 36)
  function initWeekDropdown() {
    if (!selectMatchWeek || selectMatchWeek.options.length > 0) return;
    for (let w = 1; w <= 36; w++) {
      const opt = document.createElement('option');
      opt.value = String(w);
      opt.textContent = `Week ${w}`;
      selectMatchWeek.appendChild(opt);
    }
  }

  // Get Bowler's official handicap from league secretary data or previous entry
  function getDefaultHandicap(s) {
    const bowlers = s.leagueBowlers || [];
    const currentUser = (s.currentUser || '').toLowerCase();

    let matchedBowler = null;
    if (currentUser) {
      matchedBowler = bowlers.find(b => (b.BowlerName || '').toLowerCase().includes(currentUser));
    }
    if (!matchedBowler) {
      matchedBowler = bowlers.find(b => {
        const name = (b.BowlerName || '').toLowerCase();
        return name.includes('eric') || name.includes('matson');
      });
    }

    if (matchedBowler && matchedBowler.HandicapAfterBowling !== undefined && matchedBowler.HandicapAfterBowling !== null) {
      return Number(matchedBowler.HandicapAfterBowling);
    }

    if (s.session && s.session.handicap !== undefined) {
      return Number(s.session.handicap);
    }

    return 34; // Baseline default handicap
  }

  // Determine current active week from history (auto-advances to maxWeek + 1)
  function getSuggestedActiveWeek(s) {
    if (s.session && s.session.week) {
      return Number(s.session.week);
    }

    const currentSeason = '2026-2027';
    const seasonHistory = (s.history || []).filter(h => h.season === currentSeason || !h.season);

    if (seasonHistory.length > 0) {
      const maxWeek = Math.max(0, ...seasonHistory.map(h => Number(h.week) || 0));
      if (maxWeek > 0) return maxWeek + 1; // e.g. Weeks 1-5 bowled -> automatically opens Week 6
    }

    return 1;
  }

  function populateBallSelectors() {
    const s = appState.get();
    const dropdowns = [ballSelectGame1, ballSelectGame2, ballSelectGame3];

    dropdowns.forEach((select, idx) => {
      if (!select) return;
      const curVal = select.value || (s.session && s.session[`ballGame${idx + 1}`]) || s.activeBallId;

      select.innerHTML = '<option value="">-- None --</option>';
      s.arsenal.forEach(ball => {
        const opt = document.createElement('option');
        opt.value = ball.id;
        opt.textContent = `${ball.brand} ${ball.name}`;
        if (ball.id === curVal) opt.selected = true;
        select.appendChild(opt);
      });
    });
  }

  function calculateSeriesTotals() {
    const hdcp = parseInt(inputHandicap?.value) || 0;
    const g1 = inputGame1?.value !== '' ? parseInt(inputGame1.value) : null;
    const g2 = inputGame2?.value !== '' ? parseInt(inputGame2.value) : null;
    const g3 = inputGame3?.value !== '' ? parseInt(inputGame3.value) : null;

    // Per-game handicap live preview badges
    if (g1HdcpPreview) {
      g1HdcpPreview.textContent = g1 !== null ? `+${hdcp} = ${g1 + hdcp}` : `+${hdcp} = --`;
    }
    if (g2HdcpPreview) {
      g2HdcpPreview.textContent = g2 !== null ? `+${hdcp} = ${g2 + hdcp}` : `+${hdcp} = --`;
    }
    if (g3HdcpPreview) {
      g3HdcpPreview.textContent = g3 !== null ? `+${hdcp} = ${g3 + hdcp}` : `+${hdcp} = --`;
    }

    let scratchTotal = 0;
    let count = 0;

    if (g1 !== null) { scratchTotal += g1; count++; }
    if (g2 !== null) { scratchTotal += g2; count++; }
    if (g3 !== null) { scratchTotal += g3; count++; }

    const hdcpTotal = count > 0 ? (scratchTotal + (count * hdcp)) : 0;
    const avg = count > 0 ? (scratchTotal / count).toFixed(1) : '0.0';

    if (displaySeriesTotal) displaySeriesTotal.textContent = count > 0 ? scratchTotal : '--';
    if (displayHdcpTotal) displayHdcpTotal.textContent = count > 0 ? hdcpTotal : '--';
    if (displaySeriesAvg) displaySeriesAvg.textContent = count > 0 ? avg : '--';
  }

  // Load scores for a specific week from history or session
  function loadWeekData(weekNum) {
    const s = appState.get();
    initializedWeek = weekNum;

    if (selectMatchWeek) selectMatchWeek.value = String(weekNum);
    if (scorecardHeading) scorecardHeading.textContent = `League Night — Week ${weekNum}`;

    const defaultHdcp = getDefaultHandicap(s);
    const existing = (s.history || []).find(h => 
      (h.season === '2026-2027' || !h.season) && Number(h.week) === weekNum
    );

    if (existing) {
      // Load saved scores for this week
      if (inputOpponent) inputOpponent.value = existing.opponent || '';
      if (inputHandicap) inputHandicap.value = existing.handicap !== undefined ? existing.handicap : defaultHdcp;
      if (inputGame1) inputGame1.value = existing.game1 !== null && existing.game1 !== undefined ? existing.game1 : '';
      if (inputGame2) inputGame2.value = existing.game2 !== null && existing.game2 !== undefined ? existing.game2 : '';
      if (inputGame3) inputGame3.value = existing.game3 !== null && existing.game3 !== undefined ? existing.game3 : '';
      if (inputPointsWon) inputPointsWon.value = existing.pointsWon || existing.result || '';

      if (ballSelectGame1 && existing.ballGame1) ballSelectGame1.value = existing.ballGame1;
      if (ballSelectGame2 && existing.ballGame2) ballSelectGame2.value = existing.ballGame2;
      if (ballSelectGame3 && existing.ballGame3) ballSelectGame3.value = existing.ballGame3;
    } else if (s.session && Number(s.session.week) === weekNum) {
      // Load active session for this week
      if (inputOpponent) inputOpponent.value = s.session.opponent || '';
      if (inputHandicap) inputHandicap.value = s.session.handicap !== undefined ? s.session.handicap : defaultHdcp;
      if (inputGame1) inputGame1.value = s.session.game1 !== null && s.session.game1 !== undefined ? s.session.game1 : '';
      if (inputGame2) inputGame2.value = s.session.game2 !== null && s.session.game2 !== undefined ? s.session.game2 : '';
      if (inputGame3) inputGame3.value = s.session.game3 !== null && s.session.game3 !== undefined ? s.session.game3 : '';
      if (inputPointsWon) inputPointsWon.value = s.session.pointsWon || '';
    } else {
      // Fresh new week entry
      if (inputOpponent) inputOpponent.value = '';
      if (inputHandicap) inputHandicap.value = defaultHdcp;
      if (inputGame1) inputGame1.value = '';
      if (inputGame2) inputGame2.value = '';
      if (inputGame3) inputGame3.value = '';
      if (inputPointsWon) inputPointsWon.value = '';
    }

    calculateSeriesTotals();
  }

  function updateHomeView() {
    const s = appState.get();
    initWeekDropdown();

    if (initializedWeek === null) {
      const suggestedWeek = getSuggestedActiveWeek(s);
      loadWeekData(suggestedWeek);
    }

    populateBallSelectors();
    calculateSeriesTotals();

    // Active Ball in Hand
    const activeBall = s.arsenal.find(b => b.id === s.activeBallId) || s.arsenal[0];
    if (activeBall) {
      if (homeBallName) homeBallName.textContent = `${activeBall.brand} ${activeBall.name}`;
      if (homeBallMeta) homeBallMeta.textContent = `${activeBall.coverType} • ${activeBall.finish} • ${activeBall.weight} lbs`;

      if (homeBallAvatar && activeBall.colors && activeBall.colors.length >= 2) {
        homeBallAvatar.style.background = `radial-gradient(circle at 18px 18px, ${activeBall.colors[0]} 0%, ${activeBall.colors[1]} 60%, ${activeBall.colors[2] || '#000'} 100%)`;
      }
    }

    // Lane Notes
    if (notesListEl) {
      notesListEl.innerHTML = '';
      if (s.notes.length === 0) {
        notesListEl.innerHTML = '<li class="note-item"><p class="note-text">No notes logged yet for tonight.</p></li>';
      } else {
        s.notes.forEach(note => {
          const li = document.createElement('li');
          li.className = 'note-item';
          li.innerHTML = `
            <span class="note-time">${note.context}</span>
            <p class="note-text">${note.text}</p>
          `;
          notesListEl.appendChild(li);
        });
      }
    }

    // Friction Pills
    frictionPills.forEach(pill => {
      const cond = pill.getAttribute('data-condition');
      if (cond === s.activeOilCondition) {
        pill.classList.add('active');
      } else {
        pill.classList.remove('active');
      }
    });

    if (advisorTipText && frictionTips[s.activeOilCondition]) {
      advisorTipText.querySelector('p').textContent = frictionTips[s.activeOilCondition];
    }

    if (window.lucide) window.lucide.createIcons();
  }

  // Live input events for scores and handicap
  [inputGame1, inputGame2, inputGame3, inputHandicap].forEach(input => {
    if (input) input.addEventListener('input', calculateSeriesTotals);
  });

  // Week Selector change event
  if (selectMatchWeek) {
    selectMatchWeek.addEventListener('change', () => {
      const week = parseInt(selectMatchWeek.value) || 1;
      loadWeekData(week);
    });
  }

  // Next Week button event
  if (btnNextWeek) {
    btnNextWeek.addEventListener('click', () => {
      const curWeek = parseInt(selectMatchWeek?.value) || 1;
      const nextWeek = curWeek + 1;
      if (nextWeek > 36) {
        alert('Already at maximum scheduled week (Week 36).');
        return;
      }
      loadWeekData(nextWeek);
    });
  }

  // Save Scores
  if (btnSaveScores) {
    btnSaveScores.addEventListener('click', () => {
      const weekNum = parseInt(selectMatchWeek?.value) || 1;
      const hdcp = parseInt(inputHandicap?.value) || 0;

      const b1 = ballSelectGame1?.value;
      const b2 = ballSelectGame2?.value;
      const b3 = ballSelectGame3?.value;

      const g1 = inputGame1?.value !== '' ? parseInt(inputGame1.value) : null;
      const g2 = inputGame2?.value !== '' ? parseInt(inputGame2.value) : null;
      const g3 = inputGame3?.value !== '' ? parseInt(inputGame3.value) : null;

      const sessData = {
        week: weekNum,
        handicap: hdcp,
        opponent: inputOpponent?.value.trim() || '',
        game1: g1,
        game2: g2,
        game3: g3,
        ballGame1: b1,
        ballGame2: b2,
        ballGame3: b3,
        pointsWon: inputPointsWon?.value.trim() || ''
      };

      // Count games per ball
      const usageCounts = {};
      if (g1 !== null && b1) usageCounts[b1] = (usageCounts[b1] || 0) + 1;
      if (g2 !== null && b2) usageCounts[b2] = (usageCounts[b2] || 0) + 1;
      if (g3 !== null && b3) usageCounts[b3] = (usageCounts[b3] || 0) + 1;

      const s = appState.get();

      // Update gamesBowled count for used balls
      const updatedArsenal = s.arsenal.map(ball => {
        if (usageCounts[ball.id]) {
          return {
            ...ball,
            gamesBowled: (ball.gamesBowled || 0) + usageCounts[ball.id]
          };
        }
        return ball;
      });

      // Calculate series total & avg for history
      let scratchTotal = 0;
      let count = 0;
      if (g1 !== null) { scratchTotal += g1; count++; }
      if (g2 !== null) { scratchTotal += g2; count++; }
      if (g3 !== null) { scratchTotal += g3; count++; }

      const hdcpTotal = count > 0 ? (scratchTotal + (count * hdcp)) : 0;
      const avg = count > 0 ? parseFloat((scratchTotal / count).toFixed(1)) : 0;

      const todayStr = new Date().toISOString().split('T')[0];
      const seasonName = '2026-2027';

      // Find existing history record for this week and season
      const existingHistoryIndex = (s.history || []).findIndex(h => 
        (h.season === seasonName || !h.season) && Number(h.week) === weekNum
      );

      const existingRecord = existingHistoryIndex >= 0 ? s.history[existingHistoryIndex] : null;

      const historyRecord = {
        id: existingRecord ? existingRecord.id : 'h_' + Date.now(),
        season: seasonName,
        week: weekNum,
        weekLabel: `Week ${weekNum}`,
        date: existingRecord?.date || todayStr,
        opponent: sessData.opponent || 'League Opponent',
        handicap: hdcp,
        game1: g1,
        game2: g2,
        game3: g3,
        game1Hdcp: g1 !== null ? g1 + hdcp : null,
        game2Hdcp: g2 !== null ? g2 + hdcp : null,
        game3Hdcp: g3 !== null ? g3 + hdcp : null,
        ballGame1: b1,
        ballGame2: b2,
        ballGame3: b3,
        seriesTotal: scratchTotal,
        hdcpTotal: hdcpTotal,
        seriesAvg: avg,
        pointsWon: sessData.pointsWon || '',
        result: sessData.pointsWon || '',
        notes: (s.notes && s.notes.length > 0) ? s.notes[0].text : `Week ${weekNum} match scores logged.`
      };

      let updatedHistory = s.history ? [...s.history] : [];
      if (existingHistoryIndex >= 0) {
        updatedHistory[existingHistoryIndex] = historyRecord;
      } else {
        updatedHistory.push(historyRecord);
      }

      appState.set({
        session: sessData,
        arsenal: updatedArsenal,
        history: updatedHistory
      });

      alert(`Week ${weekNum} match scores saved!\nScratch: ${scratchTotal} | Handicap: ${hdcpTotal}\nStats and ball usage updated.`);
    });
  }

  // Friction pill clicks
  frictionPills.forEach(pill => {
    pill.addEventListener('click', () => {
      const cond = pill.getAttribute('data-condition');
      appState.set({ activeOilCondition: cond });
      updateHomeView();
    });
  });

  // Note Modal handlers
  const openNoteModal = () => {
    if (modalNote) modalNote.classList.remove('hidden');
  };
  const closeNoteModal = () => {
    if (modalNote) modalNote.classList.add('hidden');
  };

  if (btnAddNoteInline) btnAddNoteInline.addEventListener('click', openNoteModal);
  if (btnCloseNote) btnCloseNote.addEventListener('click', closeNoteModal);
  if (btnCancelNote) btnCancelNote.addEventListener('click', closeNoteModal);

  if (btnSaveNote) {
    btnSaveNote.addEventListener('click', () => {
      const ctxVal = document.getElementById('noteContextInput').value.trim() || 'Game 2';
      const textVal = document.getElementById('noteTextInput').value.trim();

      if (!textVal) return;

      const newNote = {
        id: 'n_' + Date.now(),
        context: ctxVal,
        text: textVal
      };

      const s = appState.get();
      appState.set({ notes: [newNote, ...s.notes] });

      document.getElementById('noteContextInput').value = '';
      document.getElementById('noteTextInput').value = '';
      closeNoteModal();
      updateHomeView();
    });
  }

  updateHomeView();
  appState.subscribe(updateHomeView);
}
